import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { ShopProvider } from '@/context';
import { AddToCart } from '@/components/AddToCart';
import { WishlistButton } from '@/components/WishlistButton';
import { ProductPage } from '@/components/Product/ProductPage';

// Initialize Stripe
const stripeKey = window.evcShop?.stripeKey;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

// Helper to wrap with Stripe if available
const WithStripe = ({ children }: { children: React.ReactNode }) => {
  if (!stripePromise) return <>{children}</>;

  // Note: For PaymentRequestButton, we don't strictly need clientSecret initially,
  // but we need to pass currency/mode to Elements or just stripe instance.
  // Passing just stripe instance is often enough for the Element to initialize 
  // and check for wallet availability.
  return (
    <Elements stripe={stripePromise} options={{ mode: 'payment', currency: window.evcShop?.currency?.toLowerCase() || 'eur', amount: 100 }}>
      {children}
    </Elements>
  );
};

// Mount AddToCart buttons
const addToCartContainers = document.querySelectorAll('.evcs-add-to-cart');

addToCartContainers.forEach((container) => {
  const productId = parseInt(container.getAttribute('data-product-id') || '0', 10);
  const variationId = container.getAttribute('data-variation-id') || undefined;
  const stock = parseInt(container.getAttribute('data-stock') || '0', 10);
  const showQuantity = container.getAttribute('data-show-quantity') !== 'false';

  if (productId) {
    const root = createRoot(container);
    root.render(
      <StrictMode>
        <ShopProvider>
          <WithStripe>
            <AddToCart
              productId={productId}
              variationId={variationId}
              stock={stock}
              showQuantity={showQuantity}
              fullWidth
            />
          </WithStripe>
        </ShopProvider>
      </StrictMode>
    );
  }
});

// Mount Wishlist buttons
const wishlistContainers = document.querySelectorAll('.evcs-wishlist-button');

wishlistContainers.forEach((container) => {
  const productId = parseInt(container.getAttribute('data-product-id') || '0', 10);

  if (productId) {
    const root = createRoot(container);
    root.render(
      <StrictMode>
        <ShopProvider>
          <WishlistButton productId={productId} />
        </ShopProvider>
      </StrictMode>
    );
  }
});

// Mount embedded products (via shortcode)
const embeddedProducts = document.querySelectorAll('.evcs-product-embed');

embeddedProducts.forEach((container) => {
  const productId = parseInt(container.getAttribute('data-product-id') || '0', 10);
  // Attempt to read initial JSON data if present in a script tag or attribute
  // For now, we rely on ProductPage fetching or using mock if not provided
  const initialDataAttr = container.getAttribute('data-initial-product');
  const initialData = initialDataAttr ? JSON.parse(initialDataAttr) : undefined;

  if (productId) {
    const root = createRoot(container);
    root.render(
      <StrictMode>
        <ShopProvider>
          <WithStripe>
            <ProductPage productId={productId} initialData={initialData} />
          </WithStripe>
        </ShopProvider>
      </StrictMode>
    );
  }
});
